/**
 * Event Types Settings Page
 *
 * Manage event type templates for the organization.
 * Event types define reusable templates with predefined roles.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useOrganization } from '../../../context/OrganizationContext';
import { getEventTypes, EventType } from '../../../api/eventTypes';
import { getErrorMessage } from '../../../api/client';
import EventTypeCard from '../../../components/settings/EventTypeCard';
import EventTypeModal from '../../../components/settings/EventTypeModal';

export default function EventTypesPage() {
  const { currentOrg } = useOrganization();

  // State
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingEventType, setEditingEventType] = useState<EventType | null>(null);
  const [duplicatingFrom, setDuplicatingFrom] = useState<EventType | null>(null);

  // Fetch event types
  const fetchEventTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getEventTypes();
      setEventTypes(response.eventTypes);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEventTypes();
  }, [fetchEventTypes]);

  const handleCreateNew = () => {
    setEditingEventType(null);
    setDuplicatingFrom(null);
    setShowModal(true);
  };

  const handleEdit = (eventType: EventType) => {
    setDuplicatingFrom(null);
    setEditingEventType(eventType);
    setShowModal(true);
  };

  // Duplicating opens the create flow seeded from the source — nothing is
  // written until the copy is saved under its own name.
  const handleDuplicate = (eventType: EventType) => {
    setEditingEventType(null);
    setDuplicatingFrom(eventType);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingEventType(null);
    setDuplicatingFrom(null);
  };

  const handleSuccess = () => {
    fetchEventTypes();
  };

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-4">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/admin/settings" className="hover:text-gray-700">
              Settings
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="w-4 h-4" />
          </li>
          <li className="text-gray-900 font-medium">Event Types</li>
        </ol>
      </nav>

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Event Types</h1>
          <p className="text-gray-600 mt-1">
            Create templates with predefined roles for {currentOrg?.orgName || 'your organization'}
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Create Event Type
        </button>
      </div>

      {/* Info box */}
      <div className="mb-6 p-4 bg-surface-panel border border-hairline border-l-[3px] border-l-rack">
        <div className="flex">
          <InfoIcon className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">How Event Types Work</p>
            <p className="mt-1">
              Event types are templates that pre-define the roles needed for recurring event formats.
              When you create an event and select a type, the roles are automatically added, saving setup time.
            </p>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
          <p className="mt-2 text-gray-600">Loading event types...</p>
        </div>
      ) : eventTypes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <TemplateIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No event types</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating your first event type template.
          </p>
          <button
            onClick={handleCreateNew}
            className="mt-4 inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Create Event Type
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {eventTypes.map((eventType) => (
            <EventTypeCard
              key={eventType.id}
              eventType={eventType}
              onEdit={handleEdit}
              onDuplicate={handleDuplicate}
              onDeleted={handleSuccess}
              onUpdated={handleSuccess}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <EventTypeModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
        eventType={editingEventType}
        duplicateFrom={duplicatingFrom}
        existingNames={eventTypes.map((t) => t.name)}
      />
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function TemplateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
      />
    </svg>
  );
}
